import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Filter, Bookmark, BookmarkCheck, Briefcase, DollarSign } from 'lucide-react';
import { useJobs } from '../hooks/useJobs';
import { apiClient } from '@zaogig/auth';
import type { Job } from '@zaogig/types';
import { toast } from 'sonner';

/* ─── Helpers ──────────────────────────────── */

const salaryUnitLabel = (unit: string) =>
  unit === 'day' ? '日' : unit === 'hour' ? '時' : '月';

const formatSalary = (s: { min: number; max: number; unit: string }) =>
  `${s.min.toLocaleString()}–${s.max.toLocaleString()}/${salaryUnitLabel(s.unit)}`;

/* ─── JobCard ──────────────────────────────── */

function JobCard({
  job,
  isBookmarked,
  onToggleBookmark,
}: {
  job: Job;
  isBookmarked: boolean;
  onToggleBookmark: (e: React.MouseEvent) => void;
}) {
  return (
    <Link
      to={`/jobs/${job.id}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: 16,
          marginBottom: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          cursor: 'pointer',
          transition: 'transform 0.15s',
        }}
      >
        {/* Title + bookmark */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, flex: 1 }}>
            {job.title}
          </h3>
          <button
            onClick={(e) => { e.preventDefault(); onToggleBookmark(e); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            {isBookmarked ? (
              <BookmarkCheck className="w-5 h-5" style={{ color: '#1677ff' }} />
            ) : (
              <Bookmark className="w-5 h-5" style={{ color: '#bbb' }} />
            )}
          </button>
        </div>

        {/* Company */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, background: '#e6f0ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Briefcase className="w-4 h-4" style={{ color: '#1677ff' }} />
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, color: '#333', margin: 0 }}>
              {job.company.name}
            </p>
          </div>
        </div>

        {/* Location */}
        <p style={{ fontSize: 13, color: '#666', margin: '0 0 4px' }}>
          <MapPin className="w-3.5 h-3.5" style={{ display: 'inline', marginRight: 2, verticalAlign: 'text-bottom' }} />
          {job.location}
        </p>

        {/* Salary */}
        <p style={{ fontSize: 15, color: '#ff4d4f', fontWeight: 600, margin: '8px 0 0' }}>
          <DollarSign className="w-4 h-4" style={{ display: 'inline', marginRight: 1, verticalAlign: 'text-bottom', color: '#ff4d4f' }} />
          {formatSalary(job.salary)}
        </p>

        {/* Date */}
        <p style={{ fontSize: 12, color: '#999', margin: '6px 0 0' }}>
          {new Date(job.created_at).toLocaleDateString('zh-HK')}
        </p>
      </div>
    </Link>
  );
}

/* ─── Main page ────────────────────────────── */

export function JobListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [showSortMenu, setShowSortMenu] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build query params
  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (debouncedQuery) p.keyword = debouncedQuery;
    if (cityFilter) p.city = cityFilter;
    p.sort = sortBy;
    return p;
  }, [debouncedQuery, cityFilter, sortBy]);

  const { data, isLoading, isError, error } = useJobs(params);
  const jobs: Job[] = (data as any)?.data ?? (data as any) ?? [];

  // Load bookmarks on mount
  useEffect(() => {
    apiClient.get<{ data: Job[] }>('/v1/jobs/bookmarks')
      .then((res: any) => {
        const arr: Job[] = res?.data ?? res ?? [];
        setBookmarkedIds(new Set(arr.map(j => j.id)));
      })
      .catch(() => { /* silently fail */ });
  }, []);

  const handleToggleBookmark = useCallback(async (jobId: string) => {
    const wasBookmarked = bookmarkedIds.has(jobId);
    try {
      if (wasBookmarked) {
        await apiClient.post(`/v1/jobs/${jobId}/unbookmark`, {});
        setBookmarkedIds(prev => { const n = new Set(prev); n.delete(jobId); return n; });
        toast.success('已取消收藏');
      } else {
        await apiClient.post(`/v1/jobs/${jobId}/bookmark`, {});
        setBookmarkedIds(prev => new Set(prev).add(jobId));
        toast.success('已收藏');
      }
    } catch {
      toast.error('操作失败');
    }
  }, [bookmarkedIds]);

  const sortOptions = [
    { value: 'created_at', label: '最新发布' },
    { value: 'salary', label: '薪资最高' },
    { value: 'match', label: '智能排序' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Search & filter bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: '#fff', padding: '12px 16px',
        borderBottom: '1px solid #eee',
      }}>
        {/* Search input */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <div style={{
            flex: 1, position: 'relative',
            background: '#f5f5f5', borderRadius: 8, border: '1px solid #e8e8e8',
          }}>
            <Search className="w-4 h-4" style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: '#999',
            }} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索岗位、公司..."
              style={{
                width: '100%', padding: '10px 12px 10px 36px',
                border: 'none', background: 'transparent',
                fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            style={{
              padding: '8px 12px', background: '#f5f5f5', border: '1px solid #e8e8e8',
              borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Filter className="w-4 h-4" style={{ color: '#666' }} />
          </button>
        </div>

        {/* Filter chips row */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {/* City filter */}
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={{
              padding: '6px 12px', borderRadius: 16, border: '1px solid #ddd',
              fontSize: 13, background: cityFilter ? '#1677ff' : '#f5f5f5',
              color: cityFilter ? '#fff' : '#666', cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="">全部城市</option>
            <option value="香港">香港</option>
            <option value="深圳">深圳</option>
            <option value="广州">广州</option>
            <option value="澳门">澳门</option>
          </select>

          {/* Sort chips */}
          {sortOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setSortBy(opt.value); setShowSortMenu(false); }}
              style={{
                padding: '6px 14px', borderRadius: 16, border: 'none',
                fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                background: sortBy === opt.value ? '#1677ff' : '#f0f0f0',
                color: sortBy === opt.value ? '#fff' : '#666',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Job list */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            共 <strong style={{ color: '#333' }}>{jobs.length}</strong> 个岗位
          </p>
        </div>

        {isLoading && (
          <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>加载中...</p>
        )}

        {isError && (
          <p style={{ textAlign: 'center', color: '#ff4d4f', padding: 40 }}>
            載入失敗：{(error as Error)?.message ?? '未知錯誤'}
          </p>
        )}

        {!isLoading && !isError && jobs.length === 0 && (
          <p style={{ textAlign: 'center', color: '#999', marginTop: 48 }}>
            {searchQuery || cityFilter ? '未找到匹配的岗位' : '暂时沒有职位'}
          </p>
        )}

        {!isLoading && !isError && jobs.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            isBookmarked={bookmarkedIds.has(job.id)}
            onToggleBookmark={() => handleToggleBookmark(job.id)}
          />
        ))}
      </div>
    </div>
  );
}
